import {
  type AnimatedProductCheckpointsApiProps,
  AnimatedProductCheckpointsApiPropsSchema,
} from './animated-product-checkpoints'
import {
  type ArticleCardsListApiProps,
  ArticleCardsListSchema,
} from './article-cards-list'
import {
  type AuthorApiProps,
  AuthorApiPropsSchema,
} from './article-page-content'
import { type ButtonsApiProps, ButtonsApiPropsSchema } from './buttons'
import { type CategoriesApiProps, CategoriesApiPropsSchema } from './categories'
import {
  type CategoryTreeApiProps,
  CategoryTreeApiPropsSchema,
} from './category-tree'
import {
  type CmsButtonApiProps as ButtonApiProps,
  CmsButtonApiPropsSchema,
} from './cms-button'
import { type ContentApiProps, ContentApiPropsSchema } from './content'
import {
  type ContentMediaApiProps,
  ContentMediaApiPropsSchema,
} from './content-media'
import {
  type CountdownBannerApiProps,
  CountdownBannerApiPropsSchema,
} from './countdown-banner'
import type { Device } from './device'
import {
  type DiscountBannerApiProps,
  DiscountBannerApiPropsSchema,
} from './discount-banner'
import {
  type EmailCollectorApiProps,
  EmailCollectorApiPropsSchema,
} from './email-collector'
import {
  type EnhancedCarouselApiProps,
  EnhancedCarouselApiPropsSchema,
} from './enhanced-product-carousel'
import { type FaqApiProps, FaqApiPropsSchema } from './faq'
import { type ListItemApiProps, ListItemSchema } from './list-item'
import { type MediaApiProps, MediaApiPropsSchema } from './media'
import {
  type ModularCardImageApiProps,
  ModularCardImageApiPropsSchema,
  type ModularCardsCollectionApiProps as ModularCardsCarouselApiProps,
  ModularCardsCollectionApiPropsSchema,
} from './modular-card'
import {
  type PersonalizedFeedApiProps,
  PersonalizedFeedApiPropsSchema,
} from './personalized-feed'
import {
  type PressCarouselApiProps,
  PressCarouselApiPropsSchema,
} from './press-carousel-content'
import {
  type ProductGradesApiProps,
  ProductGradesApiPropsSchema,
} from './product-grades'
import {
  type ProductSpotlightApiProps,
  ProductSpotlightPropsSchema,
} from './product-spotlight'
import {
  type ProductsBlockApiProps,
  ProductsBlockApiPropsSchema,
} from './products-block'
import { type QuizApiProps, QuizApiPropsSchema } from './quiz'
import {
  type RefinedProductsApiProps,
  RefinedProductsApiPropsSchema,
} from './refined-products'
import {
  type ReassuranceApiProps,
  ReassuranceApiPropsSchema,
} from './reinsurance'
import {
  type RelatedLinkApiProps,
  RelatedLinkApiPropsSchema,
} from './related-links-content'
import { type ReviewsApiProps, ReviewsApiPropsSchema } from './reviews'
import {
  type RichTextApiProps,
  RichTextApiPropsSchema,
} from './rich-text-content'
import {
  type SectionBannerApiProps,
  SectionBannerApiPropsSchema,
} from './section-banner'
import { type SliderApiProps, SliderApiPropsSchema } from './slider'
import {
  type SocialMediaEmbedApiProps,
  SocialMediaEmbedApiPropsSchema,
} from './social-media-embed'
import {
  type TradeInFooterLinksApiProps,
  TradeInFooterLinksSchema,
} from './trade-in-footer-links'
import {
  type TradeInSearchApiProps,
  TradeInSearchSchema,
} from './trade-in-search'
import {
  type TrustpilotWidgetApiProps,
  TrustpilotWidgetApiPropsSchema,
} from './trustpilot-widget'
import { type EmbeddedYoutubeVideoApiProps, YoutubeVideoSchema } from './video'
import {
  type VideosBlockApiProps,
  VideosBlockApiPropsSchema,
} from './videos-content'

export type BlockApiPropsMap = {
  'Article-author': AuthorApiProps
  AnimatedProductCheckpoints: AnimatedProductCheckpointsApiProps
  ArticleCardsList: ArticleCardsListApiProps
  Button: ButtonApiProps
  Buttons: ButtonsApiProps
  Carousel: SliderApiProps
  Categories: CategoriesApiProps
  CategoryTree: CategoryTreeApiProps
  Content: ContentApiProps
  ContentMedia: ContentMediaApiProps
  DiscountBanner: DiscountBannerApiProps
  EmbeddedYoutubeVideo: EmbeddedYoutubeVideoApiProps
  EnhancedCarousel: EnhancedCarouselApiProps
  EmailCollector: EmailCollectorApiProps
  Faq: FaqApiProps
  ListItem: ListItemApiProps
  Media: MediaApiProps
  ModularCardsCarousel: ModularCardsCarouselApiProps
  ModularCardImage: ModularCardImageApiProps
  PersonalizedFeed: PersonalizedFeedApiProps
  ProductGrades: ProductGradesApiProps
  ProductSpotlight: ProductSpotlightApiProps
  RefinedProducts: RefinedProductsApiProps
  Quiz: QuizApiProps
  PressCarousel: PressCarouselApiProps
  Products: ProductsBlockApiProps
  UpcomingSaleCountdownBanner: CountdownBannerApiProps
  Reinsurance: ReassuranceApiProps
  RelatedLinks: RelatedLinkApiProps
  Reviews: ReviewsApiProps
  RichText: RichTextApiProps
  SectionBanner: SectionBannerApiProps
  SocialMediaEmbed: SocialMediaEmbedApiProps
  TrustpilotWidget: TrustpilotWidgetApiProps
  VideosBlock: VideosBlockApiProps
  TradeInSearch: TradeInSearchApiProps
  TradeInFooterLinks: TradeInFooterLinksApiProps
}

export type BlockType = keyof BlockApiPropsMap

export type BlockTypeApiProps = BlockApiPropsMap[BlockType]

export type _ApiBlock<T extends BlockType> = {
  id: string
  type: T
  hiddenDevices: Device[]
  props: BlockApiPropsMap[T]
}

export type ApiBlock = {
  [T in keyof BlockApiPropsMap]: _ApiBlock<T>
}[keyof BlockApiPropsMap]

/* Schemas */
export const BlockApiPropsMapSchema = {
  'Article-author': AuthorApiPropsSchema,
  AnimatedProductCheckpoints: AnimatedProductCheckpointsApiPropsSchema,
  ArticleCardsList: ArticleCardsListSchema,
  Button: CmsButtonApiPropsSchema,
  Buttons: ButtonsApiPropsSchema,
  Carousel: SliderApiPropsSchema,
  Categories: CategoriesApiPropsSchema,
  CategoryTree: CategoryTreeApiPropsSchema,
  Content: ContentApiPropsSchema,
  ContentMedia: ContentMediaApiPropsSchema,
  DiscountBanner: DiscountBannerApiPropsSchema,
  EmbeddedYoutubeVideo: YoutubeVideoSchema,
  EnhancedCarousel: EnhancedCarouselApiPropsSchema,
  EmailCollector: EmailCollectorApiPropsSchema,
  Faq: FaqApiPropsSchema,
  ListItem: ListItemSchema,
  Media: MediaApiPropsSchema,
  ModularCardsCarousel: ModularCardsCollectionApiPropsSchema,
  ModularCardImage: ModularCardImageApiPropsSchema,
  ProductGrades: ProductGradesApiPropsSchema,
  ProductSpotlight: ProductSpotlightPropsSchema,
  RefinedProducts: RefinedProductsApiPropsSchema,
  Quiz: QuizApiPropsSchema,
  PersonalizedFeed: PersonalizedFeedApiPropsSchema,
  PressCarousel: PressCarouselApiPropsSchema,
  Products: ProductsBlockApiPropsSchema,
  UpcomingSaleCountdownBanner: CountdownBannerApiPropsSchema,
  Reinsurance: ReassuranceApiPropsSchema,
  RelatedLinks: RelatedLinkApiPropsSchema,
  Reviews: ReviewsApiPropsSchema,
  RichText: RichTextApiPropsSchema,
  SectionBanner: SectionBannerApiPropsSchema,
  SocialMediaEmbed: SocialMediaEmbedApiPropsSchema,
  TrustpilotWidget: TrustpilotWidgetApiPropsSchema,
  VideosBlock: VideosBlockApiPropsSchema,
  TradeInSearch: TradeInSearchSchema,
  TradeInFooterLinks: TradeInFooterLinksSchema,
}
